import * as React from 'react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Container from '@mui/material/Container';
import { Box, Typography, Divider } from '@mui/material';
import Copyright from '../../components/Copyright';
import PasswordInput from '../../components/PasswordInput';
import GoogleLoginButton from '../../components/GoogleLoginButton';

import { AuthContext } from '../../context/AuthContext';
import { httpRequest } from '../../api/helper';
import { useNotification } from '../../context/NotificationContext';
import { API_ERROR_MESSAGE, AUTH_URLS } from '../../common/AppConstants';
import * as AppUrls from '../../common/AppUrls';
import { Helmet } from 'react-helmet';
import SignUp from './SignUp';
import logger from '../../logger';

export default function SignIn() {
  const { loggedIn } = React.useContext(AuthContext);
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const { login } = React.useContext(AuthContext);
  const [editErrors, setEditErrors] = useState({});
  const [isLoginLoading, setIsLoginLoading] = useState(false);

  const resetAllErrors = () => {
    setEditErrors({});
  };

  useEffect(() => {
    resetAllErrors();
  }, []);

  useEffect(() => {
    if (loggedIn) {
      navigate(AppUrls.DASHBOARD_URL);
    }
  }, [loggedIn, navigate]);

  const handleChange = (fieldAttr, value) => {
    // Clear error when user edits the field
    setEditErrors((prevErrors) => ({
      ...prevErrors,
      [fieldAttr]: undefined,
    }));
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    const form_data = new FormData(event.currentTarget);
    setIsLoginLoading(true); // Disable login button
    resetAllErrors();

    // Perform sign-in logic, hit the API to complete sign in
    const data = {
      event_type: "signin",
      email: form_data.get('email'),
      password: form_data.get('password'),
    };

    await httpRequest({
      method: 'POST',
      url: AUTH_URLS.EMAIL_LOGIN,
      data: data,
      onSuccess: (data) => {
        login(data.access, data.refresh);
        navigate(AppUrls.DASHBOARD_URL);
      },
      onFailure: (data) => {
        setEditErrors(data);
      },
      addNotification,
    });

    setIsLoginLoading(false);
  };

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex" />
      </Helmet>
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: 2,
            mt: 16,
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
            <LockOutlinedIcon />
          </Avatar>
          <Typography
            variant="h1"
            color="primary.main"
            sx={{ mb: 7 }}
          >
            Sign in
          </Typography>
          
          {/* Social Login */}
          <Grid container>
            <Grid item xs sx={{ textAlign: 'center', mt: 1 }}>
              <GoogleLoginButton event_type="signin" />
            </Grid>
            {/* <Grid item xs sx={{ textAlign: 'center', mt: 1 }}>
              <FacebookLoginButton event_type="signin" />
            </Grid> */}
          </Grid>

          {/* Divider with Or */}
          <Grid container>
            <Grid item xs>
              <Box sx={{ textAlign: 'center'}}>
                <Divider sx={{ mt: 4, mb: 1 }}>
                  <Typography variant="body1" color="text.secondary">
                    or
                  </Typography>
                </Divider>
              </Box>
              </Grid>
          </Grid>
          
          {/* Email/Password Based */}
          <Box
            component="form"
            onSubmit={handleLogin}
            noValidate
          >
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              onChange={(e) => handleChange('email', e.target.value)}
              error={Boolean(editErrors.email)}
              helperText={editErrors.email?.[0] || ''}
            />
            <PasswordInput handleChange={handleChange} editErrors={editErrors}/>

            {/* <Grid container>
              <Grid item xs>
                <FormControlLabel
                  control={<Checkbox value="remember" color="primary" />}
                  label="Remember me"
                />
              </Grid>
            </Grid> */}

            <Grid container justifyContent="right">
              <Grid item>
                <Link href="/forgot-password" variant="body1">
                  Forgot password?
                </Link>
              </Grid>
            </Grid>

            <Button
              type="submit"
              // onClick={handleLogin}
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={isLoginLoading}
            >
              Sign In
            </Button>
            
            {/* Sign Up Link */}
            <Grid container justifyContent="center" sx={{ mt: 1 }}>
              <Grid item
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'center',
                }}
              >
                <Typography variant="body1" color="primary.main" gutterBottom>
                  Don't have an account?
                </Typography>
                <Link href="/sign-up" variant="h4" gutterBottom>
                  Sign up for free
                </Link>
              </Grid>
            </Grid>

            {/* Terms and Policies */}
            <Grid container sx={{ mt: 10 }}>
              <Grid item xs>
                <Link
                  variant="body2"
                  color="inherit"
                  href="/terms-of-service"
                  target="_blank"
                  rel="noopener noreferrer"
                  // sx={{ textDecoration: 'none' }}
                >
                  Terms of Service
                </Link>
              </Grid>
              <Grid item>
                <Link
                  variant="body2"
                  color="inherit"
                  href="/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  // sx={{ textDecoration: 'none' }}
                >
                  Privacy Policy
                </Link>
              </Grid>
            </Grid>

            <Copyright sx={{ mt: 2 }} />
          </Box>
        </Box>
      </Container>
    </>
  );
}
